import React from 'react'
import { JsonLd } from './JsonLd'

export interface ReturnPolicyJsonLdProps {
  /** The URL of the return policy page */
  returnPolicyUrl: string
  /** The return policy category (e.g., 'limited', 'exchange_only', 'full_refund') */
  returnPolicyCategory?: string
  /** Number of days for return */
  returnWithinDays?: number
  /** Currency code (e.g., 'USD') */
  restockingFeeCurrency?: string
  /** Restocking fee amount */
  restockingFeeAmount?: number
  /** Additional return policy details */
  additionalProperty?: Array<{
    name: string
    value: string
  }>
}

interface ReturnPolicyJsonLdSchema {
  '@type': 'ReturnPolicy'
  returnPolicyCategory: string
  returnPolicyUrl: string
  returnWithin?: {
    '@type': 'QuantitativeValue'
    unitCode: 'DAY'
    value: number
  }
  restockingFee?: {
    '@type': 'MonetaryAmount'
    currency: string
    value: number
  }
  additionalProperty?: Array<{
    '@type': 'PropertyValue'
    name: string
    value: string
  }>
}

export const ReturnPolicyJsonLd: React.FC<ReturnPolicyJsonLdProps> = ({
  returnPolicyUrl,
  returnPolicyCategory = 'full_refund',
  returnWithinDays,
  restockingFeeCurrency,
  restockingFeeAmount,
  additionalProperty,
}) => {
  const returnPolicy: ReturnPolicyJsonLdSchema = {
    '@type': 'ReturnPolicy',
    returnPolicyCategory,
    returnPolicyUrl,
  }

  if (returnWithinDays) {
    returnPolicy.returnWithin = {
      '@type': 'QuantitativeValue',
      unitCode: 'DAY',
      value: returnWithinDays,
    }
  }

  if (restockingFeeCurrency && restockingFeeAmount !== undefined) {
    returnPolicy.restockingFee = {
      '@type': 'MonetaryAmount',
      currency: restockingFeeCurrency,
      value: restockingFeeAmount,
    }
  }

  if (additionalProperty && additionalProperty.length > 0) {
    returnPolicy.additionalProperty = additionalProperty.map((prop) => ({
      '@type': 'PropertyValue',
      name: prop.name,
      value: prop.value,
    }))
  }

  return <JsonLd item={returnPolicy} type={'ReturnPolicy'} scriptKey={'ReturnPolicy'} />
}
